import { RouteInfo } from "common/routes/route-info.interface";
import { routes } from "common/routes/routes";
import { Authorized } from "core/auth/Authorized";
import { ComponentType, lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";

import { NotFoundErrorBox, Overlay } from "@bps/fluent-ui";

import { MainLayout } from "./MainLayout";

interface LazyRoute extends RouteInfo {
  importComponent: () => Promise<{ default: ComponentType<any> }>;
  children?: LazyRoute[];
  index?: boolean;
}

const lazyRoutes: LazyRoute[] = [
  {
    ...routes.appointmentBooking.basePath,
    importComponent: () => import("modules/booking")
  },
  {
    ...routes.bpMobile,
    importComponent: () =>
      import("modules/bp-mobile/mobile-layout/MobileLayout"),
    children: [
      {
        ...routes.bpMobileDashboard.basePath,
        index: true,
        importComponent: () =>
          import("modules/bp-mobile/dashboard/pages/DashboardScreen")
      },
      {
        ...routes.bpMobileBooking.bookingCalendar,
        importComponent: () =>
          import("modules/bp-mobile/booking/pages/BookingCalendarScreen")
      },
      {
        ...routes.bpMobileBooking.appointments,
        importComponent: () =>
          import("modules/bp-mobile/booking/pages/ScheduleScreen")
      },
      {
        ...routes.bpMobileClinicalRecord.view,
        importComponent: () =>
          import("modules/bp-mobile/clinical/pages/ClinicalRecordScreen")
      },
      {
        ...routes.bpMobileClinicalRecord.edit,
        importComponent: () =>
          import("modules/bp-mobile/clinical/pages/ClinicalRecordEditScreen")
      },
      {
        ...routes.bpMobileTodo.userInbox,
        importComponent: () =>
          import("modules/bp-mobile/inbox/pages/InboxScreen")
      },
      {
        ...routes.bpMobileAddressBook.patients,
        importComponent: () =>
          import("modules/bp-mobile/practice/pages/AddressBookScreen")
      },
      {
        ...routes.bpMobileDocumentEditor.edit,
        importComponent: () =>
          import("modules/bp-mobile/document-editor/pages/DocumentEditorScreen")
      }
    ]
  },
  {
    ...routes.settings.basePath,
    importComponent: () => import("modules/settings/SettingsRoutes")
  }
];

// Simple wrapper component that renders the resolved lazy import
const LazyWrapper = ({ component: Component }) => <Component />;

export const Shell = () => {
  const mapLazyRoutes = (routes: LazyRoute[]) => {
    return routes.map(route => {
      const routeProps = {
        path: route.path,
        element: (
          <Authorized
            permissions={route.permissions}
            operator={route.permissionsOperator}
            customPermissionsCheck={route.customPermissionsCheck}
          >
            <LazyWrapper component={lazy(route.importComponent)} />
          </Authorized>
        )
      };

      if (route.index) {
        return (
          <Route key={route.path as string} index={true} {...routeProps} />
        );
      }

      return (
        <Route
          key={route.path as string}
          children={route.children ? mapLazyRoutes(route.children) : null}
          {...routeProps}
        />
      );
    });
  };

  return (
    <MainLayout>
      <Suspense fallback={<Overlay styles={{ root: { zIndex: 100 } }} />}>
        <Routes>
          {mapLazyRoutes(lazyRoutes)}
          <Route path="*" element={<NotFoundErrorBox showLogo />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
};
